/** @jsxImportSource @emotion/react */
"use client";

import { type Interpolation } from "@emotion/react";
import { useLocale, useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { CommunityActivityFeedReferenceType } from "@packages/sdk";
import { useQueryClient, useRequestCommunitiesAdminPost } from "@packages/sdk";
import { keyCommunityActivityFeed } from "@packages/sdk/src/data/requests/community/activityFeed/keyCommunityActivityFeed";
import type { ThemeType } from "@packages/ui";
import {
  constantSemanticColors,
  DropdownMenu,
  DropdownMenuItem,
  dropShadow,
  IconButton,
  MoreIcon,
  spacing,
  TrashIcon,
  useAlert,
} from "@packages/ui";
import { useParams } from "next/navigation";

import { useConfirmRemovePostDialog } from "../../useConfirmRemovePostDialog";

const dropdownMenuItemStyles: Interpolation = {
  columnGap: spacing.s,
  minWidth: "180px",
  padding: `${spacing.xs} ${spacing.s}`,
};

const dropdownMenuStyles: Interpolation = {
  boxShadow: `${dropShadow.none} ${dropShadow.ms} ${dropShadow.m} ${dropShadow.none} ${constantSemanticColors.fixedDarkenVeryLow}`,
};

const dropdownMenuItemErrorStyles: Interpolation<ThemeType> = (
  theme: ThemeType,
) => ({
  color: theme.semanticColors.errorMedium,
});

type RemovePostDropdownMenuProps = {
  communityPostId: number;
  type?: CommunityActivityFeedReferenceType;
};

export const RemovePostDropdownMenu = ({
  communityPostId,
  type,
}: RemovePostDropdownMenuProps) => {
  const t = useTranslations();
  const { communityId } = useParams<{ communityId: string }>();
  const alert = useAlert();
  const analytics = useAnalytics();
  const locale = useLocale();
  const queryClient = useQueryClient();

  const { mutationDelete: deletePost } = useRequestCommunitiesAdminPost({
    id: parseInt(communityId),
  });

  const dialogDelete = useConfirmRemovePostDialog();

  const handleDelete = async () => {
    analytics.track({
      event: HallowAnalyticsEvent.TappedRemovePost,
    });

    const confirm = await dialogDelete.show();
    if (!confirm) return;

    try {
      await deletePost.mutateAsync({
        communityPostId,
        type,
      });

      analytics.track({
        event: HallowAnalyticsEvent.RemovedPost,
        properties: {
          button: "remove",
          is_admin: true,
          type,
        },
      });

      queryClient.invalidateQueries({
        queryKey: [
          keyCommunityActivityFeed({
            id: parseInt(communityId),
            types: ["prompt_response", "intention"],
          }),
          locale,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          keyCommunityActivityFeed({
            id: parseInt(communityId),
            types: ["prompt_response"],
          }),
          locale,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          keyCommunityActivityFeed({
            id: parseInt(communityId),
            types: ["intention"],
          }),
          locale,
        ],
      });
    } catch (error) {
      alert.show({
        title: t("general_word_error"),
        description: error.message,
      });
    }
  };

  return (
    <DropdownMenu
      trigger={<IconButton icon={<MoreIcon />} variant="transparentNeutral" />}
      contentCSS={[dropdownMenuStyles]}
      align="end"
      contentProps={{ collisionPadding: 20, sideOffset: 5 }}
    >
      <DropdownMenuItem
        onClick={handleDelete}
        startIcon={<TrashIcon />}
        css={[dropdownMenuItemStyles, dropdownMenuItemErrorStyles]}
      >
        {t("community_remove_post_action")}
      </DropdownMenuItem>
    </DropdownMenu>
  );
};

RemovePostDropdownMenu.displayName = "ActivityCardPromptResponse";
